'use client';

import React, { useState, useRef, useEffect } from 'react';
import { useTranslations } from 'next-intl';
import { useAccessibility } from './AccessibilityProvider';

type DaltonismMode = 'none' | 'protanopia' | 'deuteranopia' | 'tritanopia';

// SVG Icons inline (pas de lucide-react dans le projet)
function IconAccessibility({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <circle cx="12" cy="4.5" r="2" />
      <path d="M12 7v4m0 0l-4 7m4-7l4 7M6 12h12" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconClose({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}

function IconReset({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 12a9 9 0 109-9 9.75 9.75 0 00-6.74 2.74L3 8" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 3v5h5" />
    </svg>
  );
}

function ToggleSwitch({
  label,
  description,
  checked,
  onChange,
  id,
}: {
  label: string;
  description: string;
  checked: boolean;
  onChange: () => void;
  id: string;
}) {
  return (
    <div
      className="flex items-center justify-between p-3 rounded-lg border border-border hover:bg-surface/50 transition-colors cursor-pointer"
      onClick={onChange}
      role="switch"
      aria-checked={checked}
      aria-labelledby={`${id}-label`}
      aria-describedby={`${id}-desc`}
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onChange();
        }
      }}
    >
      <div className="flex-1 min-w-0">
        <p id={`${id}-label`} className="text-sm font-medium text-foreground">{label}</p>
        <p id={`${id}-desc`} className="text-xs text-muted mt-0.5">{description}</p>
      </div>
      <div
        className={`relative shrink-0 w-11 h-6 rounded-full transition-colors ms-3 ${
          checked ? 'bg-primary' : 'bg-gray-300'
        }`}
      >
        <div
          className={`absolute top-0.5 start-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${
            checked ? 'translate-x-5 rtl:-translate-x-5' : 'translate-x-0'
          }`}
        />
      </div>
    </div>
  );
}

export default function AccessibilityToolbar() {
  const [isOpen, setIsOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const t = useTranslations('accessibility');

  const {
    settings,
    toggleLargeFonts,
    toggleHighContrast,
    setDaltonismMode,
    toggleReducedMotion,
    toggleFocusVisible,
    toggleDarkMode,
    toggleScreenReaderMode,
    resetSettings,
    activeCount,
  } = useAccessibility();

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsOpen(false);
        buttonRef.current?.focus();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  // Close on click outside
  useEffect(() => {
    if (!isOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (
        panelRef.current &&
        !panelRef.current.contains(e.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isOpen]);

  // Focus trap inside panel
  useEffect(() => {
    if (!isOpen || !panelRef.current) return;
    const focusableElements = panelRef.current.querySelectorAll<HTMLElement>(
      'button, [role="switch"], input, select, [tabindex]:not([tabindex="-1"])'
    );
    if (focusableElements.length > 0) {
      focusableElements[0].focus();
    }
  }, [isOpen]);

  const daltonismOptions: { value: DaltonismMode; labelKey: string; descKey: string }[] = [
    { value: 'none', labelKey: 'daltonism_none', descKey: 'daltonism_none_desc' },
    { value: 'protanopia', labelKey: 'daltonism_protanopia', descKey: 'daltonism_protanopia_desc' },
    { value: 'deuteranopia', labelKey: 'daltonism_deuteranopia', descKey: 'daltonism_deuteranopia_desc' },
    { value: 'tritanopia', labelKey: 'daltonism_tritanopia', descKey: 'daltonism_tritanopia_desc' },
  ];

  return (
    <>
      {/* Floating toggle button - bottom left */}
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-4 start-4 z-50 w-12 h-12 bg-primary text-white rounded-full shadow-lg hover:bg-primary-light transition-colors flex items-center justify-center"
        aria-label={t('toggle_panel')}
        aria-expanded={isOpen}
        aria-controls="accessibility-panel"
        title={t('title')}
      >
        <IconAccessibility className="w-6 h-6" />
        {activeCount > 0 && (
          <span
            className="absolute -top-1 -end-1 w-5 h-5 bg-secondary text-white text-[10px] font-bold rounded-full flex items-center justify-center"
            aria-label={t('active_count', { count: activeCount })}
          >
            {activeCount}
          </span>
        )}
      </button>

      {/* Panel */}
      {isOpen && (
        <div
          ref={panelRef}
          id="accessibility-panel"
          className="fixed bottom-20 start-4 z-50 bg-background rounded-xl shadow-2xl border border-border w-80 max-w-[calc(100vw-2rem)] max-h-[calc(100vh-7rem)] overflow-y-auto"
          role="dialog"
          aria-modal="true"
          aria-label={t('title')}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-border sticky top-0 bg-background rounded-t-xl z-10">
            <div className="flex items-center gap-2">
              <IconAccessibility className="w-5 h-5 text-primary" />
              <h2 className="text-base font-semibold text-foreground">{t('title')}</h2>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="p-1.5 rounded-lg hover:bg-surface transition-colors"
              aria-label={t('close')}
            >
              <IconClose className="w-4 h-4 text-muted" />
            </button>
          </div>

          {/* Content */}
          <div className="p-4 space-y-3">
            {/* Large Fonts */}
            <ToggleSwitch
              id="a11y-large-fonts"
              label={t('large_fonts')}
              description={t('large_fonts_desc')}
              checked={settings.largeFonts}
              onChange={toggleLargeFonts}
            />

            {/* High Contrast */}
            <ToggleSwitch
              id="a11y-high-contrast"
              label={t('high_contrast')}
              description={t('high_contrast_desc')}
              checked={settings.highContrast}
              onChange={toggleHighContrast}
            />

            {/* Dark Mode */}
            <ToggleSwitch
              id="a11y-dark-mode"
              label={t('dark_mode')}
              description={t('dark_mode_desc')}
              checked={settings.darkMode}
              onChange={toggleDarkMode}
            />

            {/* Daltonism Select */}
            <div className="p-3 rounded-lg border border-border">
              <label htmlFor="a11y-daltonism" className="block text-sm font-medium text-foreground mb-1">
                {t('daltonism')}
              </label>
              <p className="text-xs text-muted mb-2">{t('daltonism_desc')}</p>
              <select
                id="a11y-daltonism"
                value={settings.daltonismMode}
                onChange={(e) => setDaltonismMode(e.target.value as DaltonismMode)}
                className="w-full px-3 py-2 text-sm rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                aria-label={t('daltonism')}
              >
                {daltonismOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {t(opt.labelKey)} — {t(opt.descKey)}
                  </option>
                ))}
              </select>
            </div>

            {/* Reduced Motion */}
            <ToggleSwitch
              id="a11y-reduced-motion"
              label={t('reduced_motion')}
              description={t('reduced_motion_desc')}
              checked={settings.reducedMotion}
              onChange={toggleReducedMotion}
            />

            {/* Focus Visible */}
            <ToggleSwitch
              id="a11y-focus-visible"
              label={t('focus_visible')}
              description={t('focus_visible_desc')}
              checked={settings.focusVisible}
              onChange={toggleFocusVisible}
            />

            {/* Screen Reader Mode */}
            <ToggleSwitch
              id="a11y-screen-reader"
              label={t('screen_reader')}
              description={t('screen_reader_desc')}
              checked={settings.screenReaderMode}
              onChange={toggleScreenReaderMode}
            />

            {/* WCAG Info */}
            <div className="bg-primary/5 border border-primary/20 rounded-lg p-3">
              <p className="text-xs text-primary font-medium">{t('wcag_label')}</p>
              <p className="text-xs text-muted mt-1">{t('wcag_desc')}</p>
            </div>
          </div>

          {/* Footer: Reset */}
          <div className="p-3 border-t border-border">
            <button
              onClick={() => {
                resetSettings();
                // Announce to screen readers
                const announcer = document.getElementById('aria-announcements');
                if (announcer) announcer.textContent = t('reset_done');
              }}
              className="flex items-center justify-center gap-2 w-full py-2 rounded-lg text-sm font-medium text-muted hover:bg-surface transition-colors"
              aria-label={t('reset')}
            >
              <IconReset className="w-4 h-4" />
              {t('reset')}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
